import React, { useState, useMemo } from 'react';
import { ShieldAlert, AlertTriangle, Activity, Search, Filter, Users, ArrowRight, ChevronDown, Zap } from 'lucide-react';
import { T, TRADER_COLORS, fmtMonth, fmtNum, fmtMoney, safeNum, calcHealthScore, healthColor, healthLabel } from '../theme';
import { getGroupConfig } from '../riskConfig';
import { Badge, RiskBadge, StatusBadge, HealthBar, StatCard, Card, TabBar, DataTable, MiniBar, CoBadge, GroupBadge, HelpTip } from '../components/UI';
import {
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ComposedChart, ScatterChart, Scatter,
} from 'recharts';

// Trader type columns for Risk Monitor tab
const TRADER_COLS = [
  { type: "GainerTrader", label: "Gainer", field: "ProfitFactor", fmt: v => fmtNum(safeNum(v), 2) },
  { type: "Scalper", label: "Scalper", field: "PctUnder5Min", fmt: v => fmtNum(safeNum(v), 1) + "%" },
  { type: "HeavyTrader", label: "Heavy", field: "Volume", fmt: v => fmtNum(safeNum(v), 0) },
  { type: "HighFrequencyTrader", label: "HFT", field: "TotalTrades", fmt: v => fmtNum(safeNum(v), 0) },
  { type: "Commissioner", label: "Comm.", field: "CommissionRebateExploitation", fmt: v => fmtMoney(v) },
  { type: "HedgingAbuser", label: "H.Abuse", field: "FridayHedgeBreaks", fmt: v => safeNum(v) },
  { type: "Swaper", label: "Swaper", field: "RemainingSwap", fmt: v => fmtMoney(v) },
  { type: "NewsTrader", label: "News", field: "TradesNearNews10Min", fmt: v => safeNum(v) },
  { type: "Hedger", label: "Hedger", field: "HedgeRatio", fmt: v => fmtNum(safeNum(v), 2) },
  { type: "LongTrader", label: "Long", field: "OrdersHeldGT10Days", fmt: v => safeNum(v) },
  { type: "IP_Abuser", label: "IP", field: "IP_ODT", fmt: v => v || "No" },
];

// ── Shared filter bar (OUTSIDE component to avoid focus loss) ──
function FilterButtons({ buttons, active, setActive }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
      {buttons.map(f => (
        <button key={f.key} onClick={() => setActive(f.key)}
          style={{ padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontSize: 10, fontWeight: 700, border: "none", transition: "all 0.15s",
            background: active === f.key ? (f.color || T.text) + "25" : T.bg,
            color: active === f.key ? (f.color || T.text) : T.textMuted,
            boxShadow: active === f.key ? "inset 0 0 0 1.5px " + (f.color || T.text) + "50" : "none" }}>
          {f.label} <span style={{ opacity: 0.7, marginLeft: 3 }}>{f.count}</span>
        </button>
      ))}
    </div>
  );
}

function SearchRow({ search, setSearch, count, onClear, placeholder }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10 }}>
      <div style={{ flex: 1, position: "relative" }}>
        <Search size={16} color={T.textDim} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder={placeholder || "Search by Login, Name, or A.M..."}
          style={{ width: "100%", padding: "9px 12px 9px 36px", borderRadius: 8, border: "1px solid " + T.border, background: T.bgInput || T.bg, color: T.text, fontSize: 12, outline: "none" }} />
      </div>
      <span className="mono" style={{ fontSize: 11, color: T.textDim, whiteSpace: "nowrap" }}>{count} clients</span>
      {onClear && <button onClick={onClear}
        style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid " + T.border, background: "transparent", color: T.textMuted, cursor: "pointer", fontSize: 10, fontWeight: 600, whiteSpace: "nowrap" }}>✕ Clear</button>}
    </div>
  );
}

function AMSelect({ value, onChange, amList, totalCount }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
      <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 600 }}>Account Manager:</span>
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ padding: "6px 28px 6px 10px", borderRadius: 6, border: "1px solid " + T.border, background: T.bg, color: T.text, fontSize: 12, cursor: "pointer", outline: "none", appearance: "none",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center" }}>
        <option value="all">All ({totalCount})</option>
        {amList.map(am => <option key={am} value={am}>{am}</option>)}
      </select>
      {value !== "all" && (
        <button onClick={() => onChange("all")}
          style={{ padding: "3px 8px", borderRadius: 4, border: "1px solid " + T.border, background: "transparent", color: T.textMuted, cursor: "pointer", fontSize: 10 }}>✕</button>
      )}
    </div>
  );
}

export default function RiskMonitor({ C, onClientClick }) {
  const [tab, setTab] = useState("groups");
  const [groupFilter, setGroupFilter] = useState("all");
  const [groupSearch, setGroupSearch] = useState("");
  const [groupAM, setGroupAM] = useState("all");
  const [riskFilter, setRiskFilter] = useState("all");
  const [riskSearch, setRiskSearch] = useState("");
  const [evoFilter, setEvoFilter] = useState("all");
  const [evoSearch, setEvoSearch] = useState("");
  const [evoAM, setEvoAM] = useState("all");
  const [chgFilter, setChgFilter] = useState("all");

  const gcfg = getGroupConfig();
  const allClients = C.healthScores || C.clients;
  const groupChanges = C.groupChanges || [];

  // Arbitrage helper
  const arbCfg = gcfg.arbitrage;
  const isArbClient = c => { const cds = (arbCfg.conditions||[]).filter(x=>x.enabled); if(!cds.length) return false; return cds.every(cd => { const v=safeNum(c[cd.column]); if(cd.op==="flag") return String(c[cd.column])===cd.value; if(cd.op===">=") return v>=cd.value; if(cd.op==="<") return v>0&&v<cd.value; if(cd.op===">") return v>cd.value; return false; }); };

  // A.M list
  const amList = useMemo(() => {
    return [...new Set(allClients.map(c => c["A.M"]).filter(v => v && v !== "NULL" && v !== "X"))].sort();
  }, [allClients]);

  // ── Clients Group tab ──
  const groupKeys = gcfg.groupOrder || Object.keys(gcfg.groups);
  let grpFiltered = groupFilter === "all" ? allClients : allClients.filter(c => c._clientGroup === groupFilter);
  if (groupAM !== "all") grpFiltered = grpFiltered.filter(c => c["A.M"] === groupAM);
  const gs = groupSearch.trim().toLowerCase();
  if (gs) grpFiltered = grpFiltered.filter(c => String(c.Login||"").toLowerCase().includes(gs) || String(c.Name||"").toLowerCase().includes(gs) || String(c["A.M"]||"").toLowerCase().includes(gs));
  const grpBase = groupAM === "all" ? allClients : allClients.filter(c => c["A.M"] === groupAM);
  const grpCounts = {};
  groupKeys.forEach(gk => { grpCounts[gk] = grpBase.filter(c => c._clientGroup === gk).length; });

  // ── Risk Monitor tab ──
  const isFlagged = c => TRADER_COLS.some(tc => tc.type === "IP_Abuser" ? (c.IP_ODT || c.Login_Same_IP || c.CU_Same_IP) : c[tc.type] === tc.type) || isArbClient(c);
  const combined = allClients.filter(isFlagged);
  const highRisk = allClients.filter(c => c.BehavioralRisk === "High");
  const typeCounts = {};
  TRADER_COLS.forEach(tc => { typeCounts[tc.type] = allClients.filter(c => tc.type === "IP_Abuser" ? (c.IP_ODT || c.Login_Same_IP || c.CU_Same_IP) : c[tc.type] === tc.type).length; });
  typeCounts.ArbitrageSuspected = allClients.filter(isArbClient).length;

  let riskFiltered;
  if (riskFilter === "all") riskFiltered = combined;
  else if (riskFilter === "behavioral") riskFiltered = highRisk;
  else if (riskFilter === "IP_Abuser") riskFiltered = allClients.filter(c => c.IP_ODT || c.Login_Same_IP || c.CU_Same_IP);
  else if (riskFilter === "ArbitrageSuspected") riskFiltered = allClients.filter(isArbClient);
  else riskFiltered = allClients.filter(c => c[riskFilter] === riskFilter);
  const rs = riskSearch.trim().toLowerCase();
  if (rs) riskFiltered = riskFiltered.filter(c => String(c.Login||"").toLowerCase().includes(rs) || String(c.Name||"").toLowerCase().includes(rs) || String(c.CU_Account||"").toLowerCase().includes(rs) || String(c["A.M"]||"").toLowerCase().includes(rs));

  // ── Group Changes tab ──
  let chgFiltered = chgFilter === "all" ? groupChanges : chgFilter === "escalated" ? groupChanges.filter(c => {
    const order = groupKeys; return order.indexOf(c._newGroup) > order.indexOf(c._prevGroup);
  }) : chgFilter === "deescalated" ? groupChanges.filter(c => {
    const order = groupKeys; return order.indexOf(c._newGroup) < order.indexOf(c._prevGroup);
  }) : groupChanges.filter(c => c._newGroup === chgFilter);

  // ── Risk Evolution tab ──
  const evoClients = allClients.map(c => ({ ...c, _score: c.healthScore || calcHealthScore(c), _label: healthLabel(c.healthScore || calcHealthScore(c)) }));
  let evoBase = evoAM === "all" ? evoClients : evoClients.filter(c => c["A.M"] === evoAM);
  const evoGroups = { all: evoBase, critical: evoBase.filter(c => c._score >= 51), high: evoBase.filter(c => c._score >= 39 && c._score < 51), elevated: evoBase.filter(c => c._score >= 27 && c._score < 39), low: evoBase.filter(c => c._score >= 16 && c._score < 27), safe: evoBase.filter(c => c._score < 16) };
  let evoFiltered = evoGroups[evoFilter] || evoBase;
  const es = evoSearch.trim().toLowerCase();
  if (es) evoFiltered = evoFiltered.filter(c => String(c.Login||"").toLowerCase().includes(es) || String(c.Name||"").toLowerCase().includes(es) || String(c["A.M"]||"").toLowerCase().includes(es));

  const arbCount = typeCounts.ArbitrageSuspected;

  const tabs = [
    { id: "groups", label: "Clients Group", count: allClients.length },
    { id: "monitor", label: "Risk Monitor", count: combined.length },
    { id: "changes", label: "Group Changes", count: groupChanges.length },
    { id: "evolution", label: "Risk Evolution", count: allClients.length },
    { id: "speed", label: "Speed Profiling", icon: <Zap size={14} /> },
    { id: "arbitrage", label: "Arbitrage", count: arbCount, icon: <AlertTriangle size={14} /> },
  ];

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20 }}>Risk Monitor</h1>

      {/* Summary Cards Row 1 */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14, marginBottom: 14 }}>
        {groupKeys.slice(0, 4).map(gk => {
          const g = gcfg.groups[gk];
          return <StatCard key={gk} label={gk} value={C.groupDist?.[gk] || 0} color={g.color} sub={g.name} />;
        })}
        <StatCard label="Group Changes" value={groupChanges.length} color={T.purple} sub="vs previous month" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 14, marginBottom: 20 }}>
        {groupKeys.slice(4).map(gk => {
          const g = gcfg.groups[gk];
          return <StatCard key={gk} label={gk} value={C.groupDist?.[gk] || 0} color={g.color} sub={g.name} />;
        })}
        {arbCount > 0 && <StatCard label="Arbitrage" value={arbCount} color="#ff0055" sub="Suspected" icon={<AlertTriangle size={18} />} />}
        <StatCard label="Critical" value={C.healthDist?.critical || 0} color={T.red} sub="Score 51+" />
      </div>

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {/* ════ TAB 1: CLIENTS GROUP ════ */}
      {tab === "groups" && (
        <div>
          <Card style={{ marginBottom: 16 }}>
            <FilterButtons
              buttons={[
                { key: "all", label: "All Clients", count: grpBase.length, color: T.text },
                ...groupKeys.map(gk => ({ key: gk, label: gk, count: grpCounts[gk], color: gcfg.groups[gk]?.color || T.cyan })),
              ]}
              active={groupFilter} setActive={setGroupFilter} />
            <AMSelect value={groupAM} onChange={setGroupAM} amList={amList} totalCount={allClients.length} />
            <SearchRow search={groupSearch} setSearch={setGroupSearch} count={grpFiltered.length}
              onClear={(groupFilter !== "all" || groupSearch || groupAM !== "all") ? () => { setGroupFilter("all"); setGroupSearch(""); setGroupAM("all"); } : null} />
          </Card>
          <DataTable exportTitle="Risk-ClientGroups" pageSize={20} onRowClick={onClientClick} emptyMsg="No clients" compact
            columns={[
              { key: "Login", label: "Login", render: (v, r) => <span className="mono" style={{ display: "flex", alignItems: "center", gap: 3, color: T.cyan, fontSize: 11 }}><CoBadge co={r._company} size={14} />{v}</span> },
              { key: "Name", label: "Name", render: v => <span style={{ fontSize: 11 }}>{(v || "\u2014").substring(0, 18)}</span> },
              { key: "A.M", label: "A.M", render: v => <span style={{ fontSize: 10, color: T.textDim }}>{(v || "\u2014").substring(0, 12)}</span> },
              { key: "_clientGroup", label: "Group", render: (v, r) => <GroupBadge group={v} client={r} /> },
              { key: "healthScore", label: "Score", value: r => r.healthScore, render: (v, r) => <HealthBar score={r.healthScore} /> },
              { key: "BehavioralRisk", label: "B.Risk", render: v => <RiskBadge level={v} /> },
              { key: "ClientType", label: "Type", render: v => <span style={{ fontSize: 10, color: T.textDim }}>{v || "\u2014"}</span> },
              { key: "ProfitFactor", label: "PF", value: r => safeNum(r.ProfitFactor), render: (v, r) => <span className="mono" style={{ fontSize: 10, color: safeNum(r.ProfitFactor) >= 1.5 ? T.amber : T.textMuted }}>{fmtNum(safeNum(r.ProfitFactor), 2)}</span> },
              { key: "Equity", label: "Equity", value: r => safeNum(r.Equity), render: (v, r) => <span className="mono" style={{ fontSize: 10 }}>{fmtMoney(r.Equity)}</span> },
              { key: "Volume", label: "Volume", value: r => safeNum(r.Volume), render: (v, r) => <span className="mono" style={{ fontSize: 10 }}>{fmtNum(safeNum(r.Volume), 0)}</span> },
            ]}
            data={grpFiltered} />
        </div>
      )}

      {/* ════ TAB 2: RISK MONITOR ════ */}
      {tab === "monitor" && (
        <div>
          <Card style={{ marginBottom: 16 }}>
            <FilterButtons
              buttons={[
                { key: "all", label: "All Flagged", count: combined.length, color: T.text },
                { key: "behavioral", label: "Behavioral High", count: highRisk.length, color: T.red },
                ...TRADER_COLS.map(tc => ({ key: tc.type, label: tc.label, count: typeCounts[tc.type], color: TRADER_COLORS[tc.type] || T.cyan })),
                { key: "ArbitrageSuspected", label: "Arbitrage", count: arbCount, color: "#ff0055" },
              ]}
              active={riskFilter} setActive={setRiskFilter} />
            <SearchRow search={riskSearch} setSearch={setRiskSearch} count={riskFiltered.length}
              onClear={(riskFilter !== "all" || riskSearch) ? () => { setRiskFilter("all"); setRiskSearch(""); } : null} />
          </Card>
          <DataTable exportTitle="Risk-Monitor" pageSize={20} onRowClick={onClientClick} emptyMsg="No flagged clients" compact
            columns={[
              { key: "Login", label: "Login", render: (v, r) => <span className="mono" style={{ display: "flex", alignItems: "center", gap: 3, color: T.cyan, fontSize: 10 }}><CoBadge co={r._company} size={14} />{v}</span> },
              { key: "Name", label: "Name", render: v => <span style={{ fontSize: 10 }}>{(v || "\u2014").substring(0, 16)}</span> },
              { key: "A.M", label: "A.M", render: v => <span style={{ fontSize: 9, color: T.textDim }}>{(v || "\u2014").substring(0, 10)}</span> },
              { key: "_clientGroup", label: "Group", render: (v, r) => <GroupBadge group={v} client={r} /> },
              { key: "healthScore", label: "Score", value: r => r.healthScore, render: (v, r) => <HealthBar score={r.healthScore} /> },
              ...TRADER_COLS.map(tc => ({
                key: tc.field, label: tc.label,
                value: r => tc.type === "IP_Abuser" ? (r.IP_ODT ? 1 : 0) : safeNum(r[tc.field]),
                render: (v, r) => {
                  const flagged = tc.type === "IP_Abuser" ? (r.IP_ODT || r.Login_Same_IP || r.CU_Same_IP) : r[tc.type] === tc.type;
                  return <span className="mono" style={{ fontSize: 10, fontWeight: flagged ? 700 : 400, color: flagged ? (TRADER_COLORS[tc.type] || T.cyan) : T.textMuted }}>{tc.fmt(r[tc.field])}</span>;
                }, align: "center",
              })),
            ]}
            data={riskFiltered} />
        </div>
      )}

      {/* ════ TAB 3: GROUP CHANGES ════ */}
      {tab === "changes" && (
        <div>
          <Card style={{ marginBottom: 16 }}>
            <FilterButtons
              buttons={[
                { key: "all", label: "All Changes", count: groupChanges.length, color: T.text },
                { key: "escalated", label: "Escalated", count: groupChanges.filter(c => { const o = groupKeys; return o.indexOf(c._newGroup) > o.indexOf(c._prevGroup); }).length, color: T.red },
                { key: "deescalated", label: "De-escalated", count: groupChanges.filter(c => { const o = groupKeys; return o.indexOf(c._newGroup) < o.indexOf(c._prevGroup); }).length, color: T.green },
                ...groupKeys.map(gk => ({ key: gk, label: `\u2192 ${gk}`, count: groupChanges.filter(c => c._newGroup === gk).length, color: gcfg.groups[gk]?.color || T.cyan })),
              ].filter(f => f.count > 0)}
              active={chgFilter} setActive={setChgFilter} />
          </Card>
          <DataTable exportTitle="Risk-GroupChanges" pageSize={20} onRowClick={onClientClick} emptyMsg="No group changes" compact
            columns={[
              { key: "Login", label: "Login", render: (v, r) => <span className="mono" style={{ display: "flex", alignItems: "center", gap: 3, color: T.cyan, fontSize: 11 }}><CoBadge co={r._company} size={14} />{v}</span> },
              { key: "Name", label: "Name", render: v => <span style={{ fontSize: 11 }}>{(v || "\u2014").substring(0, 18)}</span> },
              { key: "_prevGroup", label: "From", render: (v, r) => <GroupBadge group={v} client={r} /> },
              { key: "arrow", label: "", render: () => <ArrowRight size={14} color={T.textMuted} />, align: "center" },
              { key: "_newGroup", label: "To", render: (v, r) => <GroupBadge group={v} client={r} /> },
              { key: "healthScore", label: "Score", value: r => r.healthScore, render: (v, r) => <HealthBar score={r.healthScore} /> },
              { key: "BehavioralRisk", label: "B.Risk", render: v => <RiskBadge level={v} /> },
              { key: "A.M", label: "A.M", render: v => <span style={{ fontSize: 10, color: T.textDim }}>{(v || "\u2014").substring(0, 12)}</span> },
              { key: "Equity", label: "Equity", value: r => safeNum(r.Equity), render: (v, r) => <span className="mono" style={{ fontSize: 10 }}>{fmtMoney(r.Equity)}</span> },
            ]}
            data={chgFiltered} />
        </div>
      )}

      {/* ════ TAB 4: RISK EVOLUTION ════ */}
      {tab === "evolution" && (
        <div>
          <Card style={{ marginBottom: 16 }}>
            <FilterButtons
              buttons={[
                { key: "all", label: "All Clients", count: evoGroups.all.length, color: T.text },
                { key: "critical", label: "Critical (51+)", count: evoGroups.critical.length, color: T.red },
                { key: "high", label: "High (39-50)", count: evoGroups.high.length, color: T.orange },
                { key: "elevated", label: "Elevated (27-38)", count: evoGroups.elevated.length, color: T.amber },
                { key: "low", label: "Low (16-26)", count: evoGroups.low.length, color: T.cyan },
                { key: "safe", label: "Safe (0-15)", count: evoGroups.safe.length, color: T.green },
              ]}
              active={evoFilter} setActive={setEvoFilter} />
            <AMSelect value={evoAM} onChange={setEvoAM} amList={amList} totalCount={allClients.length} />
            <SearchRow search={evoSearch} setSearch={setEvoSearch} count={evoFiltered.length}
              onClear={(evoFilter !== "all" || evoSearch || evoAM !== "all") ? () => { setEvoFilter("all"); setEvoSearch(""); setEvoAM("all"); } : null} />
          </Card>
          <div style={{ display: "flex", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, marginRight: 4 }}>Risk:</span>
            {[
              { label: "Safe", color: T.green }, { label: "Low", color: T.cyan },
              { label: "Elevated", color: T.amber }, { label: "High", color: T.orange },
              { label: "Critical", color: T.red },
            ].map(l => (
              <span key={l.label} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.textDim }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: l.color, display: "inline-block" }} /> {l.label}
              </span>
            ))}
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 600, marginRight: 4 }}>Group:</span>
            {(gcfg.groupOrder || Object.keys(gcfg.groups)).map(gk => { const gDef = gcfg.groups[gk]; if (!gDef) return null; return (
              <span key={gk} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.textDim }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: gDef.color, display: "inline-block" }} /> {gk}
              </span>
            ); })}
          </div>
          <DataTable exportTitle="Risk-Evolution" pageSize={20} onRowClick={onClientClick} emptyMsg="No clients" compact
            columns={[
              { key: "Login", label: "Login", render: (v, r) => <span className="mono" style={{ display: "flex", alignItems: "center", gap: 6, color: T.cyan }}><CoBadge co={r._company} size={16} />{v}</span> },
              { key: "Name", label: "Name", render: v => (v || "\u2014").substring(0, 20) },
              { key: "_score", label: "Score", value: r => r._score, render: (v, r) => <span className="mono" style={{ color: healthColor(r._score), fontWeight: 700 }}>{r._score}</span> },
              { key: "_label", label: "Level", render: (v, r) => <Badge text={r._label} color={healthColor(r._score)} bg={healthColor(r._score) + "18"} /> },
              { key: "_clientGroup", label: "Group", render: (v, r) => <GroupBadge group={v} client={r} /> },
              { key: "A.M", label: "A.M", render: v => <span style={{ fontSize: 11, color: T.textDim }}>{(v || "\u2014").substring(0, 12)}</span> },
              { key: "history", label: "Risk Timeline", width: 240, render: (v, r) => {
                const history = C.historyMap?.[r.Login] || [];
                if (!history.length) return <span style={{ color: T.textMuted, fontSize: 11 }}>No history</span>;
                return (
                  <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
                    {history.map((h, i) => {
                      const sc = calcHealthScore(h);
                      const bg = sc >= 51 ? T.red : sc >= 39 ? T.orange : sc >= 27 ? T.amber : sc >= 16 ? T.cyan : T.green;
                      const lbl = sc >= 51 ? "Critical" : sc >= 39 ? "High" : sc >= 27 ? "Elevated" : sc >= 16 ? "Low" : "Safe";
                      return <div key={i} title={fmtMonth(h.MonthKey) + " \u2014 Score: " + sc + " (" + lbl + ")"} style={{ width: 18, height: 14, borderRadius: 2, background: bg, opacity: 0.85, flexShrink: 0 }} />;
                    })}
                  </div>
                );
              }},
              { key: "groupHistory", label: "Group Timeline", width: 240, render: (v, r) => {
                const gh = C.groupHistoryMap?.[r.Login] || [];
                if (!gh.length) return <span style={{ color: T.textMuted, fontSize: 11 }}>—</span>;
                return (
                  <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
                    {gh.map((h, i) => {
                      const gDef = gcfg.groups[h.group];
                      const col = gDef?.color || T.textMuted;
                      return <div key={i} title={fmtMonth(h.month) + " \u2014 " + h.group + " (Score: " + h.score + ")"} style={{ width: 18, height: 14, borderRadius: 2, background: col, opacity: 0.85, flexShrink: 0 }} />;
                    })}
                  </div>
                );
              }},
            ]}
            data={evoFiltered} />
        </div>
      )}

      {/* ════ TAB 5: SPEED PROFILING ════ */}
      {tab === "speed" && (() => {
        const active = allClients.filter(c => safeNum(c.TotalTrades) > 0);
        const shortNum = v => v >= 1e6 ? (v / 1e6).toFixed(1) + "M" : v >= 1e3 ? (v / 1e3).toFixed(0) + "K" : v;
        // Speed layer distributions
        const makeDist = (field, label) => {
          const vals = active.map(c => safeNum(c[field]));
          return [
            { range: "0%", min: 0, max: 0, count: vals.filter(v => v === 0).length },
            { range: "1-10%", min: 0.01, max: 10, count: vals.filter(v => v > 0 && v <= 10).length },
            { range: "10-20%", min: 10, max: 20, count: vals.filter(v => v > 10 && v <= 20).length },
            { range: "20-30%", min: 20, max: 30, count: vals.filter(v => v > 20 && v <= 30).length },
            { range: "30-50%", min: 30, max: 50, count: vals.filter(v => v > 30 && v <= 50).length },
            { range: "50-70%", min: 50, max: 70, count: vals.filter(v => v > 50 && v <= 70).length },
            { range: "70-90%", min: 70, max: 90, count: vals.filter(v => v > 70 && v <= 90).length },
            { range: "90-100%", min: 90, max: 100, count: vals.filter(v => v > 90).length },
          ];
        };
        const dist30s = makeDist("PctUnder30Sec", "<30 Sec");
        const dist2m = makeDist("PctUnder2Min", "<2 Min");
        const dist5m = makeDist("PctUnder5Min", "<5 Min");

        // Scalper tiers
        const tierOf = c => {
          const p30 = safeNum(c.PctUnder30Sec), p2 = safeNum(c.PctUnder2Min), p5 = safeNum(c.PctUnder5Min);
          if (p30 > 50 || (p30 > 30 && safeNum(c.SpeedConsistency) < 0.15)) return "Extreme";
          if (p30 > 20 || p2 > 50) return "Heavy";
          if (p30 > 10 || p2 > 30 || p5 > 60) return "Scalper";
          if (p2 > 15 || p5 > 40) return "Light";
          return "Normal";
        };
        const tierColors = { Normal: T.green, Light: T.cyan, Scalper: T.amber, Heavy: T.orange, Extreme: T.red };
        const tierCounts = {};
        ["Normal", "Light", "Scalper", "Heavy", "Extreme"].forEach(t => { tierCounts[t] = active.filter(c => tierOf(c) === t).length; });
        const tierData = Object.entries(tierCounts).map(([tier, count]) => ({ tier, count, pct: active.length > 0 ? (count / active.length * 100).toFixed(1) : 0 }));

        // SpeedConsistency distribution
        const scVals = active.map(c => safeNum(c.SpeedConsistency)).filter(v => v > 0);
        const scDist = [
          { range: "<0.1", min: 0, max: 0.1, count: scVals.filter(v => v > 0 && v < 0.1).length, color: T.red },
          { range: "0.1-0.2", min: 0.1, max: 0.2, count: scVals.filter(v => v >= 0.1 && v < 0.2).length, color: T.red },
          { range: "0.2-0.3", min: 0.2, max: 0.3, count: scVals.filter(v => v >= 0.2 && v < 0.3).length, color: T.orange },
          { range: "0.3-0.4", min: 0.3, max: 0.4, count: scVals.filter(v => v >= 0.3 && v < 0.4).length, color: T.orange },
          { range: "0.4-0.5", min: 0.4, max: 0.5, count: scVals.filter(v => v >= 0.4 && v < 0.5).length, color: T.amber },
          { range: "0.5-0.6", min: 0.5, max: 0.6, count: scVals.filter(v => v >= 0.5 && v < 0.6).length, color: T.amber },
          { range: "0.6-0.8", min: 0.6, max: 0.8, count: scVals.filter(v => v >= 0.6 && v < 0.8).length, color: T.cyan },
          { range: "0.8-1.0", min: 0.8, max: 1.0, count: scVals.filter(v => v >= 0.8 && v <= 1.0).length, color: T.green },
        ];

        // Fast vs Slow win rate
        const wrData = active.filter(c => safeNum(c.FastTradeWinRate) > 0 || safeNum(c.SlowTradeWinRate) > 0).map(c => ({
          fast: safeNum(c.FastTradeWinRate), slow: safeNum(c.SlowTradeWinRate),
          login: c.Login, name: c.Name, tier: tierOf(c),
        }));
        const wrBuckets = [
          { label: "0-20%", min: 0, max: 20 }, { label: "20-40%", min: 20, max: 40 },
          { label: "40-60%", min: 40, max: 60 }, { label: "60-80%", min: 60, max: 80 },
          { label: "80-100%", min: 80, max: 100 },
        ].map(b => ({
          range: b.label,
          fast: wrData.filter(d => d.fast >= b.min && d.fast < (b.max === 100 ? 101 : b.max)).length,
          slow: wrData.filter(d => d.slow >= b.min && d.slow < (b.max === 100 ? 101 : b.max)).length,
        }));

        // Top speed offenders
        const topSpeed = [...active].sort((a, b) => safeNum(b.PctUnder30Sec) - safeNum(a.PctUnder30Sec)).slice(0, 15);

        const CustomTT = ({ active: act, payload }) => {
          if (!act || !payload?.length) return null;
          return (<div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 11 }}>
            {payload.map((p, i) => <div key={i} style={{ color: p.color || T.text }}>{p.name}: <b>{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</b></div>)}
          </div>);
        };

        return (
          <div>
            {/* Summary cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 20 }}>
              {tierData.map(t => (
                <div key={t.tier} style={{ background: T.bgCard, borderRadius: 10, border: `1px solid ${T.border}`, padding: "16px 14px", borderLeft: `4px solid ${tierColors[t.tier]}` }}>
                  <div style={{ fontSize: 11, color: tierColors[t.tier], textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, marginBottom: 6 }}>{t.tier}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                    <span className="mono" style={{ fontSize: 26, fontWeight: 800, color: T.text }}>{t.count}</span>
                    <span style={{ fontSize: 12, color: T.textDim, fontWeight: 600 }}>{t.pct}%</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Row 1: Tier Pie + Speed Consistency */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <Card accent={T.amber}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>Scalper Tier Distribution <HelpTip id="speedTiers" /></div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={tierData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                    <XAxis dataKey="tier" tick={{ fontSize: 11, fill: T.text }} />
                    <YAxis tick={{ fontSize: 9, fill: T.textDim }} />
                    <Tooltip content={<CustomTT />} />
                    <Bar dataKey="count" name="Clients" radius={[4, 4, 0, 0]}>
                      {tierData.map((d, i) => <Cell key={i} fill={tierColors[d.tier]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ display: "flex", gap: 12, marginTop: 8, justifyContent: "center", flexWrap: "wrap" }}>
                  {tierData.map(t => (
                    <span key={t.tier} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: T.textDim }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: tierColors[t.tier] }} /> {t.tier}: {t.count}
                    </span>
                  ))}
                </div>
              </Card>
              <Card accent={T.orange}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>Speed Consistency Distribution <HelpTip id="speedConsistency" /></div>
                  <span style={{ fontSize: 10, color: T.textDim }}>Lower = more bot-like</span>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={scDist}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                    <XAxis dataKey="range" tick={{ fontSize: 10, fill: T.textDim }} />
                    <YAxis tick={{ fontSize: 9, fill: T.textDim }} />
                    <Tooltip content={<CustomTT />} />
                    <Bar dataKey="count" name="Clients" radius={[4, 4, 0, 0]}>
                      {scDist.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ display: "flex", gap: 14, marginTop: 8, justifyContent: "center" }}>
                  {[{ label: "Bot-like (<0.3)", color: T.red }, { label: "Suspicious (0.3-0.5)", color: T.orange }, { label: "Normal (>0.5)", color: T.green }].map(l => (
                    <span key={l.label} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: T.textDim }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: l.color }} /> {l.label}
                    </span>
                  ))}
                </div>
              </Card>
            </div>

            {/* Row 2: 3 Speed Layer Distributions */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
              {[
                { data: dist30s, label: "Layer 1: < 30 Seconds", color: T.red, accent: T.red },
                { data: dist2m, label: "Layer 2: < 2 Minutes", color: T.orange, accent: T.orange },
                { data: dist5m, label: "Layer 3: < 5 Minutes", color: T.amber, accent: T.amber },
              ].map(layer => (
                <Card key={layer.label} accent={layer.accent}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>{layer.label}</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={layer.data}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                      <XAxis dataKey="range" tick={{ fontSize: 8, fill: T.textDim }} angle={-30} textAnchor="end" height={40} />
                      <YAxis tick={{ fontSize: 9, fill: T.textDim }} />
                      <Tooltip content={<CustomTT />} />
                      <Bar dataKey="count" name="Clients" fill={layer.color} radius={[3, 3, 0, 0]} opacity={0.85} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              ))}
            </div>

            {/* Row 3: Fast vs Slow Win Rate + Top Offenders */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <Card accent={T.cyan}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Fast Trade Win Rate vs Slow Trade Win Rate</div>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={wrBuckets}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                    <XAxis dataKey="range" tick={{ fontSize: 10, fill: T.textDim }} />
                    <YAxis tick={{ fontSize: 9, fill: T.textDim }} />
                    <Tooltip content={<CustomTT />} />
                    <Bar dataKey="fast" name="Fast (<5min) WR" fill={T.red} radius={[3, 3, 0, 0]} opacity={0.8} />
                    <Bar dataKey="slow" name="Slow (≥5min) WR" fill={T.cyan} radius={[3, 3, 0, 0]} opacity={0.8} />
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ display: "flex", gap: 16, marginTop: 8, justifyContent: "center" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.textDim }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: T.red }} /> Fast Trade WR
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.textDim }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: T.cyan }} /> Slow Trade WR
                  </span>
                </div>
              </Card>
              <Card accent={T.red}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Top 15 Speed Offenders (by % Under 30s)</div>
                <div style={{ overflowY: "auto", maxHeight: 300 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                        {["Client", "Tier", "<30s", "<2m", "<5m", "Consist.", "Fast WR"].map(h => (
                          <th key={h} style={{ padding: "5px 6px", textAlign: h === "Client" ? "left" : "center", color: T.textMuted, fontWeight: 700, fontSize: 9, textTransform: "uppercase" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {topSpeed.map((c, i) => {
                        const tier = tierOf(c);
                        return (
                          <tr key={i} onClick={() => onClientClick(c)} style={{ borderBottom: `1px solid ${T.border}20`, cursor: "pointer" }}
                            onMouseEnter={e => e.currentTarget.style.background = T.bgHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            <td style={{ padding: "5px 6px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                <CoBadge co={c._company} size={14} />
                                <span style={{ color: T.text, fontWeight: 600, fontSize: 11 }}>{(c.Name || c.Login + "").substring(0, 16)}</span>
                              </div>
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, fontWeight: 700, background: tierColors[tier] + "20", color: tierColors[tier] }}>{tier}</span>
                            </td>
                            <td className="mono" style={{ textAlign: "center", color: safeNum(c.PctUnder30Sec) > 30 ? T.red : safeNum(c.PctUnder30Sec) > 10 ? T.orange : T.textDim, fontWeight: 600 }}>{fmtNum(safeNum(c.PctUnder30Sec), 1)}%</td>
                            <td className="mono" style={{ textAlign: "center", color: safeNum(c.PctUnder2Min) > 50 ? T.red : safeNum(c.PctUnder2Min) > 20 ? T.orange : T.textDim }}>{fmtNum(safeNum(c.PctUnder2Min), 1)}%</td>
                            <td className="mono" style={{ textAlign: "center", color: safeNum(c.PctUnder5Min) > 70 ? T.red : safeNum(c.PctUnder5Min) > 40 ? T.orange : T.textDim }}>{fmtNum(safeNum(c.PctUnder5Min), 1)}%</td>
                            <td className="mono" style={{ textAlign: "center", color: safeNum(c.SpeedConsistency) < 0.3 ? T.red : safeNum(c.SpeedConsistency) < 0.5 ? T.orange : T.green, fontWeight: 600 }}>{fmtNum(safeNum(c.SpeedConsistency), 2)}</td>
                            <td className="mono" style={{ textAlign: "center", color: safeNum(c.FastTradeWinRate) > 80 ? T.red : safeNum(c.FastTradeWinRate) > 60 ? T.orange : T.textDim }}>{fmtNum(safeNum(c.FastTradeWinRate), 1)}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </div>
        );
      })()}

      {/* ════ TAB 6: ARBITRAGE ANALYSIS ════ */}
      {tab === "arbitrage" && (() => {
        const shortNum = v => v >= 1e6 ? (v / 1e6).toFixed(1) + "M" : v >= 1e3 ? (v / 1e3).toFixed(0) + "K" : v;
        const arbClients = allClients.filter(isArbClient);
        const arbConditions = (arbCfg.conditions || []).filter(c => c.enabled);

        // Per-condition hit rate among arb clients
        const condHits = arbConditions.map(cd => {
          const hits = arbClients.filter(c => {
            const v = safeNum(c[cd.column]);
            if (cd.op === "flag") return String(c[cd.column]) === cd.value;
            if (cd.op === ">=") return v >= cd.value;
            if (cd.op === "<") return v > 0 && v < cd.value;
            if (cd.op === ">") return v > cd.value;
            return false;
          }).length;
          return { ...cd, hits, pct: arbClients.length > 0 ? (hits / arbClients.length * 100).toFixed(0) : 0 };
        });

        // Near-miss clients: match N-1 conditions (close to arbitrage but not flagged)
        const nonArb = allClients.filter(c => !isArbClient(c) && safeNum(c.TotalTrades) > 0);
        const nearMiss = nonArb.map(c => {
          let matched = 0;
          arbConditions.forEach(cd => {
            const v = safeNum(c[cd.column]);
            if (cd.op === "flag" && String(c[cd.column]) === cd.value) matched++;
            else if (cd.op === ">=" && v >= cd.value) matched++;
            else if (cd.op === "<" && v > 0 && v < cd.value) matched++;
            else if (cd.op === ">" && v > cd.value) matched++;
          });
          return { ...c, _arbMatched: matched, _arbTotal: arbConditions.length };
        }).filter(c => c._arbMatched >= Math.max(arbConditions.length - 1, 1) && c._arbMatched < arbConditions.length)
          .sort((a, b) => b._arbMatched - a._arbMatched).slice(0, 30);

        // Distribution charts for arb clients vs all
        const pfBuckets = [
          { label: "<1", min: 0, max: 1 }, { label: "1-1.5", min: 1, max: 1.5 }, { label: "1.5-2", min: 1.5, max: 2 },
          { label: "2-3", min: 2, max: 3 }, { label: "3-5", min: 3, max: 5 }, { label: "5-10", min: 5, max: 10 }, { label: "10+", min: 10, max: 9999 },
        ].map(b => {
          const all = allClients.filter(c => safeNum(c.ProfitFactor) >= b.min && safeNum(c.ProfitFactor) < b.max && safeNum(c.TotalTrades) > 0).length;
          const arb = arbClients.filter(c => safeNum(c.ProfitFactor) >= b.min && safeNum(c.ProfitFactor) < b.max).length;
          return { range: b.label, all, arb };
        });

        const durBuckets = [
          { label: "<30s", min: 0, max: 30 }, { label: "30s-2m", min: 30, max: 120 }, { label: "2-5m", min: 120, max: 300 },
          { label: "5-15m", min: 300, max: 900 }, { label: "15m-1h", min: 900, max: 3600 }, { label: "1h+", min: 3600, max: 999999 },
        ].map(b => {
          const all = allClients.filter(c => { const v = safeNum(c.AvgTradeDuration); return v >= b.min && v < b.max && v > 0; }).length;
          const arb = arbClients.filter(c => { const v = safeNum(c.AvgTradeDuration); return v >= b.min && v < b.max; }).length;
          return { range: b.label, all, arb };
        });

        const wrBuckets = [
          { label: "0-30%", min: 0, max: 30 }, { label: "30-50%", min: 30, max: 50 }, { label: "50-60%", min: 50, max: 60 },
          { label: "60-70%", min: 60, max: 70 }, { label: "70-80%", min: 70, max: 80 }, { label: "80-90%", min: 80, max: 90 }, { label: "90-100%", min: 90, max: 101 },
        ].map(b => {
          const all = allClients.filter(c => { const v = safeNum(c.ProfitPct); return v >= b.min && v < b.max && safeNum(c.TotalTrades) > 0; }).length;
          const arb = arbClients.filter(c => { const v = safeNum(c.ProfitPct); return v >= b.min && v < b.max; }).length;
          return { range: b.label, all, arb };
        });

        // Arb client aggregates
        const avgPF = arbClients.length > 0 ? (arbClients.reduce((s, c) => s + safeNum(c.ProfitFactor), 0) / arbClients.length).toFixed(2) : "—";
        const avgDur = arbClients.length > 0 ? Math.round(arbClients.reduce((s, c) => s + safeNum(c.AvgTradeDuration), 0) / arbClients.length) : 0;
        const avgWR = arbClients.length > 0 ? (arbClients.reduce((s, c) => s + safeNum(c.ProfitPct), 0) / arbClients.length).toFixed(1) : "—";
        const totalProfit = arbClients.reduce((s, c) => s + safeNum(c.Profit), 0);
        const totalVol = arbClients.reduce((s, c) => s + safeNum(c.Volume), 0);

        const CustomTT = ({ active: act, payload }) => {
          if (!act || !payload?.length) return null;
          return (<div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 11 }}>
            {payload.map((p, i) => <div key={i} style={{ color: p.color || T.text }}>{p.name}: <b>{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</b></div>)}
          </div>);
        };

        return (
          <div>
            {/* Summary Cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 20 }}>
              {[
                { label: "Suspected", value: arbClients.length, color: "#ff0055", sub: `of ${allClients.length} clients` },
                { label: "Avg Profit Factor", value: avgPF, color: T.amber, sub: "arb clients" },
                { label: "Avg Duration", value: avgDur < 60 ? avgDur + "s" : (avgDur / 60).toFixed(1) + "m", color: T.cyan, sub: "avg trade time" },
                { label: "Avg Win Rate", value: avgWR + "%", color: T.green, sub: "arb clients" },
                { label: "Near-Miss", value: nearMiss.length, color: T.orange, sub: `${arbConditions.length - 1}/${arbConditions.length} conditions` },
              ].map(s => (
                <div key={s.label} style={{ background: T.bgCard, borderRadius: 10, border: `1px solid ${T.border}`, padding: "16px 14px", borderLeft: `4px solid ${s.color}` }}>
                  <div style={{ fontSize: 11, color: s.color, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, marginBottom: 6 }}>{s.label}</div>
                  <div className="mono" style={{ fontSize: 24, fontWeight: 800, color: T.text }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: T.textDim, marginTop: 4 }}>{s.sub}</div>
                </div>
              ))}
            </div>

            {/* Row 1: Condition Breakdown + Arb Client Financials */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <Card accent={"#ff0055"}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>Detection Conditions ({arbConditions.length} active) <HelpTip id="arbOverview" /></div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {condHits.map((cd, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                          <span style={{ fontSize: 11, color: T.text, fontWeight: 600 }}>{cd.label} {cd.op !== "flag" ? cd.value : ""}</span>
                          <span className="mono" style={{ fontSize: 11, color: "#ff0055", fontWeight: 700 }}>{cd.hits}/{arbClients.length} ({cd.pct}%)</span>
                        </div>
                        <div style={{ height: 6, background: T.bgHover, borderRadius: 3, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: cd.pct + "%", background: "#ff0055", borderRadius: 3, minWidth: cd.hits > 0 ? 2 : 0 }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {arbConditions.length === 0 && <div style={{ color: T.textMuted, fontSize: 12, padding: 20, textAlign: "center" }}>No conditions enabled. Configure in Admin → Group Settings.</div>}
              </Card>
              <Card accent={T.amber}>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Arbitrage Client Impact</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  {[
                    { label: "Total Profit", value: fmtMoney(totalProfit), color: totalProfit >= 0 ? T.green : T.red },
                    { label: "Total Volume", value: shortNum(Math.round(totalVol)) + " lots", color: T.cyan },
                    { label: "% of All Clients", value: (allClients.length > 0 ? (arbClients.length / allClients.length * 100).toFixed(2) : 0) + "%", color: "#ff0055" },
                    { label: "% of Total Profit", value: (() => { const tp = allClients.reduce((s, c) => s + safeNum(c.Profit), 0); return tp !== 0 ? (totalProfit / tp * 100).toFixed(1) + "%" : "—"; })(), color: T.amber },
                  ].map(m => (
                    <div key={m.label} style={{ background: T.bg, borderRadius: 8, padding: "14px 12px", borderLeft: `3px solid ${m.color}` }}>
                      <div style={{ fontSize: 10, color: T.textMuted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>{m.label}</div>
                      <div className="mono" style={{ fontSize: 18, fontWeight: 800, color: m.color }}>{m.value}</div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            {/* Row 2: Distribution Charts — Arb vs All */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
              {[
                { data: pfBuckets, label: "Profit Factor Distribution", xLabel: "Profit Factor" },
                { data: durBuckets, label: "Trade Duration Distribution", xLabel: "Avg Duration" },
                { data: wrBuckets, label: "Win Rate Distribution", xLabel: "Win Rate %" },
              ].map(chart => (
                <Card key={chart.label} accent={"#ff0055"}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>{chart.label}</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={chart.data}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.border} />
                      <XAxis dataKey="range" tick={{ fontSize: 9, fill: T.textDim }} />
                      <YAxis tick={{ fontSize: 9, fill: T.textDim }} />
                      <Tooltip content={<CustomTT />} />
                      <Bar dataKey="all" name="All Clients" fill={T.textMuted} opacity={0.4} radius={[2, 2, 0, 0]} />
                      <Bar dataKey="arb" name="Arbitrage" fill={"#ff0055"} radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              ))}
            </div>
            <div style={{ display: "flex", gap: 16, marginBottom: 16, justifyContent: "center" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.textDim }}>
                <span style={{ width: 12, height: 12, borderRadius: 2, background: T.textMuted, opacity: 0.4 }} /> All Clients
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.textDim }}>
                <span style={{ width: 12, height: 12, borderRadius: 2, background: "#ff0055" }} /> Arbitrage Suspected
              </span>
            </div>

            {/* Row 3: Arb Clients Table + Near Miss */}
            <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 16, marginBottom: 16 }}>
              <Card accent={"#ff0055"}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>Arbitrage Clients ({arbClients.length})</div>
                </div>
                <div style={{ overflowY: "auto", maxHeight: 400 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                        {["Client", "Group", "PF", "WR%", "Avg Dur", "Volume", "Profit", "Score"].map(h => (
                          <th key={h} style={{ padding: "5px 6px", textAlign: h === "Client" ? "left" : "center", color: T.textMuted, fontWeight: 700, fontSize: 9, textTransform: "uppercase" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {arbClients.sort((a, b) => safeNum(b.ProfitFactor) - safeNum(a.ProfitFactor)).map((c, i) => (
                        <tr key={i} onClick={() => onClientClick(c)} style={{ borderBottom: `1px solid ${T.border}20`, cursor: "pointer" }}
                          onMouseEnter={e => e.currentTarget.style.background = T.bgHover} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          <td style={{ padding: "5px 6px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <CoBadge co={c._company} size={14} />
                              <span style={{ color: T.text, fontWeight: 600 }}>{(c.Name || c.Login + "").substring(0, 16)}</span>
                            </div>
                          </td>
                          <td style={{ textAlign: "center" }}><GroupBadge group={c._clientGroup} client={c} /></td>
                          <td className="mono" style={{ textAlign: "center", color: safeNum(c.ProfitFactor) >= 3 ? T.red : T.amber, fontWeight: 700 }}>{fmtNum(safeNum(c.ProfitFactor), 2)}</td>
                          <td className="mono" style={{ textAlign: "center", color: safeNum(c.ProfitPct) >= 80 ? T.red : T.amber }}>{fmtNum(safeNum(c.ProfitPct), 1)}%</td>
                          <td className="mono" style={{ textAlign: "center", color: T.text }}>{(() => { const d = safeNum(c.AvgTradeDuration); return d < 60 ? d + "s" : (d / 60).toFixed(1) + "m"; })()}</td>
                          <td className="mono" style={{ textAlign: "center", color: T.cyan }}>{shortNum(Math.round(safeNum(c.Volume)))}</td>
                          <td className="mono" style={{ textAlign: "center", color: safeNum(c.Profit) >= 0 ? T.green : T.red, fontWeight: 600 }}>{fmtMoney(c.Profit)}</td>
                          <td style={{ textAlign: "center" }}><HealthBar score={c.healthScore} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {arbClients.length === 0 && <div style={{ color: T.green, fontSize: 12, padding: 20, textAlign: "center" }}>No arbitrage clients detected ✓</div>}
                </div>
              </Card>
              <Card accent={T.orange}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>Near-Miss Clients ({nearMiss.length}) <HelpTip id="arbNearMiss" /></div>
                  <span style={{ fontSize: 10, color: T.textDim }}>Missing 1 condition</span>
                </div>
                <div style={{ overflowY: "auto", maxHeight: 400 }}>
                  {nearMiss.length === 0 ? (
                    <div style={{ color: T.textMuted, fontSize: 12, padding: 20, textAlign: "center" }}>No near-miss clients found</div>
                  ) : (
                    <div>
                      {nearMiss.map((c, i) => {
                        // Find which condition they're missing
                        const missing = arbConditions.filter(cd => {
                          const v = safeNum(c[cd.column]);
                          if (cd.op === "flag") return String(c[cd.column]) !== cd.value;
                          if (cd.op === ">=") return v < cd.value;
                          if (cd.op === "<") return !(v > 0 && v < cd.value);
                          if (cd.op === ">") return v <= cd.value;
                          return true;
                        });
                        return (
                          <div key={i} onClick={() => onClientClick(c)}
                            style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", marginBottom: 4, borderRadius: 8, cursor: "pointer", background: `${T.orange}08` }}
                            onMouseEnter={e => e.currentTarget.style.background = T.bgHover} onMouseLeave={e => e.currentTarget.style.background = `${T.orange}08`}>
                            <CoBadge co={c._company} size={14} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 11, color: T.text, fontWeight: 600 }}>{(c.Name || c.Login + "").substring(0, 18)}</div>
                              <div style={{ fontSize: 9, color: T.textDim }}>{c._arbMatched}/{c._arbTotal} conditions • Missing: {missing.map(m => m.label.replace(/[≥<]/g, "").trim()).join(", ")}</div>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <div className="mono" style={{ fontSize: 10, color: T.amber }}>PF: {fmtNum(safeNum(c.ProfitFactor), 2)}</div>
                              <div className="mono" style={{ fontSize: 9, color: T.textDim }}>{fmtNum(safeNum(c.ProfitPct), 1)}% WR</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
